import { Link, NavLink, Navigate, Outlet } from "react-router-dom";
import {
  BarChart3,
  Briefcase,
  FileText,
  GraduationCap,
  Handshake,
  Inbox,
  LayoutList,
  LogOut,
  Newspaper,
  Search,
  Settings,
  Sparkles,
  Users,
  UsersRound,
} from "lucide-react";
import { useAuth } from "./AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: BarChart3, end: true },
  { to: "/admin/players", label: "Players", icon: Users },
  { to: "/admin/news", label: "News", icon: Newspaper },
  { to: "/admin/plans", label: "Plans", icon: LayoutList },
  { to: "/admin/team", label: "Positions", icon: UsersRound },
  { to: "/admin/partners", label: "Partners", icon: Handshake },
  { to: "/admin/services", label: "Services", icon: Sparkles },
  { to: "/admin/counties", label: "Counties", icon: LayoutList },
  { to: "/admin/discoveries", label: "AI Discoveries", icon: Sparkles },
  { to: "/admin/bio-review", label: "AI biographies", icon: FileText },
  { to: "/admin/data", label: "Data health", icon: BarChart3 },
  { to: "/admin/trainings", label: "Training", icon: GraduationCap },
  { to: "/admin/jobs", label: "Jobs", icon: Briefcase },
  { to: "/admin/submissions", label: "Submissions", icon: Inbox },
  { to: "/admin/intake", label: "Community intake", icon: Inbox },
  { to: "/admin/applications", label: "County proposals", icon: Inbox },
  { to: "/admin/projects", label: "County projects", icon: LayoutList },
  { to: "/admin/search", label: "Search engines", icon: Search },
  { to: "/admin/settings", label: "Site settings", icon: Settings },
  { to: "/admin/kenya-entity", label: "Kenyan entity", icon: Settings },
  { to: "/admin/licences", label: "Image licences", icon: FileText },
  { to: "/admin/consent-log", label: "Consent log", icon: FileText },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();

  if (user === null)
    return <div className="flex min-h-screen items-center justify-center text-zinc-600">Checking session…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  return (
    <div className="flex min-h-screen bg-[#050505]" data-testid="admin-shell">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 flex-col border-r border-zinc-900 bg-ink-surface md:flex">
        <Link to="/" className="flex items-center gap-2 border-b border-zinc-900 px-6 py-5">
          <span className="flex h-8 w-8 items-center justify-center bg-maasai font-display font-bold">K</span>
          <span className="font-display text-xs uppercase tracking-[0.18em] text-zinc-400">Admin</span>
        </Link>
        <nav className="flex-1 overflow-y-auto py-4">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-[0.72rem] font-bold uppercase tracking-[0.14em] transition-colors ${
                  isActive ? "bg-[#1a1a1a] text-ochre" : "text-zinc-500 hover:text-white"
                }`
              }
            >
              <n.icon size={15} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-900 px-6 py-5">
          <p className="text-xs text-white" data-testid="admin-user-name">{user.name}</p>
          <p className="mt-0.5 text-[0.62rem] uppercase tracking-[0.18em] text-maasai" data-testid="admin-user-role">
            {user.role.replace("_", " ")}
          </p>
          <button onClick={logout} className="mt-4 flex items-center gap-2 text-[0.68rem] uppercase tracking-[0.14em] text-zinc-500 hover:text-kenya-red" data-testid="admin-logout">
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <div className="no-scrollbar flex gap-1 overflow-x-auto border-b border-zinc-900 bg-ink-surface px-3 py-2 md:hidden">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `shrink-0 px-3 py-2 text-[0.65rem] font-bold uppercase tracking-[0.12em] ${
                  isActive ? "bg-[#1a1a1a] text-ochre" : "text-zinc-500"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <main className="px-5 py-8 md:px-10 md:py-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
